import { FC, useCallback } from "react";

import { AnalyticsEvent } from "@repo/edtech-analytics";
import {
  getApiUrl,
  getUploadUrl,
  revalidateCourse,
} from "@repo/edtech-api";
import { EdtechPdfPlayer } from "@repo/edtech-pdf-player";
import { EdtechScormPlayer } from "@repo/edtech-scorm-player";
import { normalizeUrl, useIsMobile } from "@repo/edtech-shared";
import { EdtechVideoPlayer } from "@repo/edtech-video-player";

import { getJSONPayload } from "../../../lib";
import {
  BorderRadiusSize,
  ExtendedLesson,
  LESSON_BORDER_RADIUS_SIZES,
  LESSON_MOBILE_BORDER_RADIUS_SIZES,
  LessonType,
  useAnalyticsContext,
  useIdContext,
} from "../../../model";
import { getConfig } from "../../../origins";
import {
  Audio,
  Longread,
  Photo,
  Presentation,
  Test,
} from "../lesson-variations";
import { Folder } from "../lesson-variations/folder/Folder";
import { Longread2 } from "../lesson-variations/longread2/Longread2";
import { Longread3 } from "../lesson-variations/longread3/Longread3";
import { LessonWrapper } from "../lesson-wrapper";

type Props = {
  publicationId: string,
  lesson: ExtendedLesson,
  showSuccessBlock?: boolean,
  onCloseSuccessBlock?: () => void,
};

export const Lesson: FC<Props> = ({
  publicationId,
  lesson,
  showSuccessBlock,
  onCloseSuccessBlock,
}) => {
  const isMobile = useIsMobile(768);
  const {
    publicationId: mainPublicationId,
    unitId: mainUnitId,
  } = useIdContext();
  const { onFinishLesson } = useAnalyticsContext();

  const { isNeedDownload, videoWithoutBorderRadius } = getConfig();

  const onPdfCompleted = useCallback(() => {
    onFinishLesson(
      { type: AnalyticsEvent.Completed }, true, true,
    );
  }, [ onFinishLesson ]);

  const onPdfDownload = useCallback(() => {
    onFinishLesson(
      { type: AnalyticsEvent.Downloaded }, true, true,
    );
  }, [ onFinishLesson ]);

  const onPassedScorm = useCallback(() => {
    revalidateCourse(mainPublicationId, mainUnitId);
  }, [ mainPublicationId, mainUnitId ]);

  const onScormFullscreenChange = useCallback((data: { isFullscreen: boolean }) => {
    document.dispatchEvent(new CustomEvent("edtech-scorm-player-fullscreen", {
      detail: data,
      bubbles: true,
    }));
  }, []);

  if (!lesson) return <>Not supported lesson</>;

  const radiusSize = LESSON_BORDER_RADIUS_SIZES[ lesson.materialType ] ?? BorderRadiusSize.M;
  const mobileRadiusSize = LESSON_MOBILE_BORDER_RADIUS_SIZES[ lesson.materialType ] ?? BorderRadiusSize.M;

  const content = (() => {
    switch (lesson.materialType) {
      case LessonType.Scorm:
        return (
          <EdtechScormPlayer
            key={lesson.nodeId}
            file={normalizeUrl(`${getApiUrl()}/assets/`, lesson.file)}
            publicationid={publicationId}
            scormid={lesson.uid}
            unitid={lesson.unitid}
            onFullscreenChange={onScormFullscreenChange}
            onPassedScorm={onPassedScorm}
          />
        );
      case LessonType.Test:
        return (
          <Test
            lesson={
              {
                ...lesson,
                payload: getJSONPayload(lesson.payload),
              }
            }
          />
        );
      case LessonType.Video:
        return (
          <EdtechVideoPlayer
            id={lesson.nodeId}
            isNeedDownload={isNeedDownload?.video}
            preview={getUploadUrl(lesson.preview)}
            src={getUploadUrl(lesson.file)}
            title={lesson.title}
            withoutBorderRadius={videoWithoutBorderRadius}
          />
        );
      case LessonType.Audio:
        return (
          <Audio
            id={lesson.nodeId}
            isNeedDownload={isNeedDownload?.audio}
            src={getUploadUrl(lesson.file)}
            title={lesson.title}
          />
        );
      case LessonType.Pdf:
        return (
          <EdtechPdfPlayer
            analyticsCompletedCallback={onPdfCompleted}
            analyticsDownloadCallback={onPdfDownload}
            filePath={getUploadUrl(lesson.file)}
            isNeedDownload={isNeedDownload?.pdf}
            title={lesson.title}
          />
        );
      case LessonType.Longread:
        return (
          <Longread
            description={lesson.richcontent}
            onFinishLesson={onFinishLesson}
          />
        );
      case LessonType.Photo:
      case LessonType.Photo2:
        return (
          <Photo
            lesson={lesson}
            onFinishLesson={onFinishLesson}
          />
        );
      case LessonType.Presentation:
        return (
          <Presentation
            lesson={lesson}
            onFinishLesson={onFinishLesson}
          />
        );
      case LessonType.Folder:
        return (
          <Folder
            lesson={lesson}
            onFinishLesson={onFinishLesson}
          />
        );
      case LessonType.Longread2:
        return (
          <Longread2
            lesson={lesson}
            onFinishLesson={onFinishLesson}
          />
        );
      case LessonType.Longread3:
        return (
          <Longread3
            lesson={lesson}
            onFinishLesson={onFinishLesson}
          />
        );
      default:
        return <>Not supported</>;
    }
  })();

  return (
    <LessonWrapper
      isBlocked={lesson?.hideCard}
      radiusSize={
        isMobile ?
          mobileRadiusSize :
          radiusSize
      }
      showSuccessBlock={showSuccessBlock}
      onCloseSuccessBlock={onCloseSuccessBlock}
    >
      {content}
    </LessonWrapper>
  );
};
